import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Anchor, Table } from '@zillow/constellation';
import { HeaderTooltip } from '@zg-rentals/rental-revenue-components';
import {
  useRentalManagerUserPropertyContactsWebApiHandler_getCountForListings,
  useRentalManagerUserPropertyToursWebApiHandler_getCountForListings,
} from '@zg-rentals/rental-manager-env';

import PropertyRow from '../../soh/PropertyRow';

import { useSelector } from '../../../selectors/useSelector';
import { AppSelectors } from '../../../selectors';

import PropertyConstants from '../../../constants/PropertyConstants';
import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';
import Add3DToursCSEvents from '../../../model/ClickstreamEvent/Add3DToursCSEvents';

import { StyledTable, StyledTableContainer, StyledTableHeaderCell } from './StyledComponents';

import PropertiesActions from '../../../actions/PropertiesActions';

import type { WebAnalyticsLeaseDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { PropertyProps } from '../../../model/Property';
import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';

const { PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const PropertiesViewList = ({ listings }: { listings: Array<PropertyProps> }) => {
  const dispatch = useDispatch();
  const { user, isEnterpriseCustomer, listingAliases } = useSelector((state) => ({
    user: state.user,
    isEnterpriseCustomer: state.user.isEnterpriseCustomer,
    listingAliases: state.properties.properties.listingAliases,
  }));

  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const isScreenLgGTE = useSelector((state) => AppSelectors.makeIsScreenLgGTE(state));
  const isScreenXlGTE = useSelector((state) => AppSelectors.makeIsScreenXlGTE(state));

  const tableRef = useRef<HTMLDivElement | null>();

  const [showMoreListingAlias, setShowMoreListingAlias] = useState('');
  const [hoveredCols, setHoveredCols] = useState<Record<string, boolean>>({});

  const show3D = isEnterpriseCustomer && isScreenXlGTE;

  const tooltip = {
    title: '3D Tour ',
    message: (
      <>
        Take your rental listing to the next level with 3D tours. Use&nbsp;
        <Anchor
          target="_blank"
          href="https://www.zillow.com/z/3d-home/"
          data-eventid={Add3DToursCSEvents.PropertiesPageOurApp.data_event_id}
          onClick={() => Add3DToursCSEvents.PropertiesPageOurApp({ user })}
        >
          our app
        </Anchor>
        &nbsp;to create free Zillow 3D Home(R) Tours or&nbsp;
        <Anchor
          target="_blank"
          href="https://showingtimeplus.com/solutions/zillow-media-experts/"
          data-eventid={Add3DToursCSEvents.PropertiesPageHirePhotographer.data_event_id}
          onClick={() => Add3DToursCSEvents.PropertiesPageHirePhotographer({ user })}
        >
          hire a photographer
        </Anchor>
        &nbsp;for professional capture. You can also add 3D tours from approved external providers.
      </>
    ),
  };

  useEffect(() => {
    document.addEventListener('mouseup', closeOpenMenus);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeOpenMenus(e);
      }
    });

    return () => {
      document.removeEventListener('mouseup', closeOpenMenus);
      document.removeEventListener('keydown', closeOpenMenus);
    };
  }, [showMoreListingAlias]);

  const {
    isLoading: isTourCountLoading,
    isSuccess: isTourCountsSuccess,
    data: tourCountData,
  } = useRentalManagerUserPropertyToursWebApiHandler_getCountForListings(
    { listingAliases },
    {
      enabled: show3D,
    },
  );

  useEffect(() => {
    if (isTourCountsSuccess) {
      dispatch(PropertiesActions.updateListingsWithTourCount(tourCountData));
    }
  }, [isTourCountsSuccess, tourCountData]);

  const {
    isLoading: isLeadCountsLoading,
    isSuccess: isLeadCountsSuccess,
    data: leadCountData,
  } = useRentalManagerUserPropertyContactsWebApiHandler_getCountForListings(
    { listingAliases },
    {
      enabled: isEnterpriseCustomer && isScreenLgGTE,
    },
  );

  useEffect(() => {
    if (isLeadCountsSuccess) {
      dispatch(PropertiesActions.updateListingsWithLeadCount(leadCountData));
    }
  }, [isLeadCountsSuccess, leadCountData]);

  const handleUpsellCellHover = (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => {
    // only fire the cs event if we have not already fired one for an cell in this column
    if (!hoveredCols[columnLabel]) {
      PropertiesPageCSEvents.PropertyCardHover({
        user,
        currentProperty,
        lease,
        triggerObject:
          PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS[
            columnLabel as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS
          ],
      });
      setHoveredCols((prev) => ({ ...prev, [columnLabel]: true }));
    }
  };

  const closeOpenMenus = (e: MouseEvent | KeyboardEvent) => {
    if (tableRef.current && showMoreListingAlias && !tableRef.current.contains(e.target as Node)) {
      setTimeout(() => {
        setShowMoreListingAlias('');
      }, 0);
    }
  };

  return (
    <StyledTableContainer ref={tableRef} isZram={isEnterpriseCustomer}>
      <StyledTable
        appearance="horizontal"
        aria-label="Medium-sized grid table"
        size="md"
        data-testid="PropertiesViewList"
        isZram={isEnterpriseCustomer}
      >
        {isEnterpriseCustomer && (
          <Table.Header>
            <Table.Row>
              {isScreenLgGTE && <StyledTableHeaderCell />}
              <StyledTableHeaderCell>Address</StyledTableHeaderCell>
              {show3D && (
                <StyledTableHeaderCell>
                  <HeaderTooltip title={tooltip.title} message={tooltip.message} titleFlex="0 0 auto" />
                </StyledTableHeaderCell>
              )}
              {isEnterpriseCustomer && isScreenLgGTE && (
                <>
                  <StyledTableHeaderCell>Listing source</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Advertising</StyledTableHeaderCell>
                </>
              )}
              <StyledTableHeaderCell>Status</StyledTableHeaderCell>
              {!isScreenMobile && <StyledTableHeaderCell>Actions</StyledTableHeaderCell>}
            </Table.Row>
          </Table.Header>
        )}
        <Table.Body>
          {listings.map((listing, index) => (
            <PropertyRow
              isTourCountLoading={isTourCountLoading}
              isLeadsCountLoading={isLeadCountsLoading}
              key={index}
              listing={listing}
              showMoreListingAlias={showMoreListingAlias}
              setShowMoreListingAlias={setShowMoreListingAlias}
              onUpsellCellHover={handleUpsellCellHover}
            />
          ))}
        </Table.Body>
      </StyledTable>
    </StyledTableContainer>
  );
};

export { PropertiesViewList };
export default PropertiesViewList;
